/**
 * Client-side Insight Preferences options and helpers.
 * Keep labels in sync with config/insightPreferences.js on the server.
 */

export const INDUSTRY_OPTIONS = [
  'Aerospace & Defence',
  'Agriculture',
  'Automotive & Mobility',
  'Construction & Infrastructure',
  'Consulting & Professional Services',
  'Consumer & Retail',
  'Education',
  'Energy & Utilities',
  'Financial Services',
  'Food & Beverage',
  'Government & Public Sector',
  'Healthcare & Life Sciences',
  'Hospitality & Travel',
  'Legal',
  'Manufacturing',
  'Media & Entertainment',
  'Non-profit / Third Sector',
  'Real Estate',
  'Technology & Software',
  'Telecommunications',
  'Transportation & Logistics',
];

const LEGACY_INDUSTRY_MAP = {
  Aerospace: 'Aerospace & Defence',
  Defence: 'Aerospace & Defence',
  Defense: 'Aerospace & Defence',
  Automotive: 'Automotive & Mobility',
  Construction: 'Construction & Infrastructure',
  Consulting: 'Consulting & Professional Services',
  Retail: 'Consumer & Retail',
  Fashion: 'Consumer & Retail',
  Energy: 'Energy & Utilities',
  Finance: 'Financial Services',
  Government: 'Government & Public Sector',
  Healthcare: 'Healthcare & Life Sciences',
  Hospitality: 'Hospitality & Travel',
  'Non-profit': 'Non-profit / Third Sector',
  Nonprofit: 'Non-profit / Third Sector',
  Technology: 'Technology & Software',
  Transportation: 'Transportation & Logistics',
  Sales: null,
  Marketing: null,
};

export const SUGGESTED_INDUSTRIES = [
  'Technology & Software',
  'Financial Services',
  'Healthcare & Life Sciences',
  'Consumer & Retail',
];

export const TECHNOLOGY_OPTIONS = [
  'Artificial Intelligence',
  'Machine Learning',
  'Robotics',
  'Cybersecurity',
  'Data Analytics',
  'Cloud Computing',
  'IoT',
  'Edge Computing',
  '5G',
  'Quantum Computing',
  'Blockchain',
  'Automation',
  'SaaS',
  'Virtual Reality',
  'Augmented Reality',
];

const LEGACY_TECHNOLOGY_MAP = {
  'Big Data': 'Data Analytics',
  'API Development': null,
  'Mobile Development': null,
  'Web Development': null,
  DevOps: null,
};

export const SUGGESTED_TECHNOLOGIES = ['Artificial Intelligence', 'Cybersecurity', 'SaaS'];

export const SUGGESTED_INTERESTS = ['Clean energy', 'Private markets', 'Digital health'];

export const SUGGESTED_ORGANISATIONS = ['Shopify', 'Siemens', 'Unilever'];

export const SUGGESTED_WATCHLIST = ['Stripe', 'Pfizer', 'HSBC'];

export const EVENT_TYPE_OPTIONS = [
  'Networking Events',
  'Conferences',
  'Workshops',
  'Seminars',
  'Trade Shows',
  'Product Launches',
  'Webinars',
  'Panel Discussions',
  'Startup Pitches',
  'Industry Meetups',
  'Training Sessions',
  'Awards Ceremonies',
  'Exhibitions',
  'Hackathons',
  'Innovation Summits',
  'Business Breakfasts',
];

export const SUGGESTED_EVENT_TYPES = ['Conferences', 'Networking Events', 'Workshops'];

export const COMPANY_SCALE_OPTIONS = [
  { value: 'Startups', label: 'Startups', hint: 'Early-stage and founding teams' },
  { value: 'Scale-ups', label: 'Scale-ups', hint: 'High-growth, expanding companies' },
  { value: 'Established businesses', label: 'Established businesses', hint: 'SMEs and mid-market firms' },
  { value: 'Large enterprises', label: 'Large enterprises', hint: 'Multinationals and listed groups' },
];

const COMPANY_SCALE_VALUES = COMPANY_SCALE_OPTIONS.map((o) => o.value);

const LEGACY_BUSINESS_STAGE_MAP = {
  'Startup (Pre-seed)': 'Startups',
  'Startup (Seed)': 'Startups',
  'Startup (Series A)': 'Startups',
  'Startup (Series B+)': 'Startups',
  'Looking to Start': 'Startups',
  'Small Business': 'Established businesses',
  'Medium Enterprise': 'Established businesses',
  'Large Enterprise': 'Large enterprises',
  'Public Company': 'Large enterprises',
  'Non-profit': null,
  'Freelancer/Consultant': null,
  'Investor/VC': null,
};

export const MARKET_REGION_OPTIONS = ['Europe', 'Middle East', 'Asia-Pacific'];

export const COUNTRY_OPTIONS = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahrain',
  'Bangladesh',
  'Belarus',
  'Belgium',
  'Bosnia and Herzegovina',
  'Brazil',
  'Bulgaria',
  'Cambodia',
  'Canada',
  'Chile',
  'China',
  'Colombia',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Egypt',
  'Estonia',
  'Ethiopia',
  'Finland',
  'France',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kuwait',
  'Latvia',
  'Lebanon',
  'Lithuania',
  'Luxembourg',
  'Malaysia',
  'Malta',
  'Mexico',
  'Morocco',
  'Netherlands',
  'New Zealand',
  'Nigeria',
  'Norway',
  'Oman',
  'Pakistan',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Saudi Arabia',
  'Serbia',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'South Africa',
  'South Korea',
  'Spain',
  'Sweden',
  'Switzerland',
  'Taiwan',
  'Thailand',
  'Turkey',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Vietnam',
];

export const MARKET_OPTIONS = [...MARKET_REGION_OPTIONS, ...COUNTRY_OPTIONS];

export const SUGGESTED_MARKETS = ['United Kingdom', 'United States', 'Europe', 'Middle East', 'Asia-Pacific'];

export const CITY_OPTIONS_BY_COUNTRY = {
  'United States': [
    'New York',
    'Los Angeles',
    'San Francisco',
    'Chicago',
    'Boston',
    'Seattle',
    'Austin',
    'Miami',
    'Washington DC',
    'Atlanta',
  ],
  'United Kingdom': [
    'London',
    'Manchester',
    'Birmingham',
    'Edinburgh',
    'Glasgow',
    'Bristol',
    'Liverpool',
    'Leeds',
    'Sheffield',
    'Cambridge',
  ],
  Canada: ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'],
  Australia: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'],
  Germany: ['Berlin', 'Munich', 'Frankfurt', 'Hamburg', 'Cologne'],
  France: ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice'],
  Netherlands: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven'],
  Japan: ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama'],
  India: ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai'],
  Singapore: ['Singapore'],
  'Hong Kong': ['Hong Kong'],
  Switzerland: ['Zurich', 'Geneva', 'Basel', 'Bern'],
  Ireland: ['Dublin', 'Cork', 'Galway'],
  Spain: ['Madrid', 'Barcelona', 'Valencia'],
  Italy: ['Milan', 'Rome', 'Turin'],
  'United Arab Emirates': ['Dubai', 'Abu Dhabi'],
};

export const LIMITS = {
  industries: 8,
  technologies: 8,
  eventTypes: 16,
  businessStages: 4,
  preferredCountries: 10,
  companies: 12,
  interestTags: 12,
};

export const EMPTY_PREFERENCES = {
  industries: [],
  technologies: [],
  companies: [],
  eventTypes: [],
  businessStages: [],
  customInterests: '',
  locationPreferences: {
    useCurrentLocation: true,
    preferredCountries: [],
    preferredCities: [],
    locationOverride: { country: '', city: '' },
    followCurrentMarket: false,
    currentMarketCountry: '',
    currentMarketUpdatedAt: null,
  },
};

function uniqueStrings(arr, max, options = {}) {
  const list = Array.isArray(arr) ? arr.map((s) => String(s).trim()).filter(Boolean) : [];
  if (!options.caseInsensitive) {
    return [...new Set(list)].slice(0, typeof max === 'number' ? max : list.length);
  }
  const seen = new Set();
  const out = [];
  for (const value of list) {
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.slice(0, typeof max === 'number' ? max : out.length);
}

function mapLabeledValues(list, canonicalList, legacyMap, limit) {
  const canonical = new Set(canonicalList);
  const values = [];
  const extras = [];
  const seen = new Set();
  const input = Array.isArray(list) ? list : [];
  for (const raw of input) {
    const value = String(raw || '').trim();
    if (!value) continue;
    let mapped = value;
    if (Object.prototype.hasOwnProperty.call(legacyMap, value)) {
      mapped = legacyMap[value];
      if (!mapped) {
        extras.push(value);
        continue;
      }
    } else if (!canonical.has(value)) {
      mapped = value;
    }
    if (seen.has(mapped)) continue;
    seen.add(mapped);
    values.push(mapped);
  }
  return {
    values: values.slice(0, typeof limit === 'number' ? limit : values.length),
    extras,
  };
}

export function normalizeIndustries(list) {
  return mapLabeledValues(list, INDUSTRY_OPTIONS, LEGACY_INDUSTRY_MAP, LIMITS.industries);
}

export function normalizeTechnologies(list) {
  return mapLabeledValues(list, TECHNOLOGY_OPTIONS, LEGACY_TECHNOLOGY_MAP, LIMITS.technologies);
}

export function parseInterestTags(text, max = LIMITS.interestTags) {
  if (!text || typeof text !== 'string') return [];
  return uniqueStrings(
    text.split(/[,;\n]/).map((t) => t.trim()),
    max,
    { caseInsensitive: true }
  ).filter((t) => t.length > 1);
}

export function serializeInterestTags(tags) {
  return uniqueStrings(tags, LIMITS.interestTags, { caseInsensitive: true }).join(', ');
}

export function mergeInterestTags(existing, extras) {
  return serializeInterestTags([...parseInterestTags(existing, 40), ...(Array.isArray(extras) ? extras : [])]);
}

export function normalizeBusinessStages(stages) {
  if (!Array.isArray(stages)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of stages) {
    const value = String(raw || '').trim();
    if (!value) continue;
    let mapped;
    if (Object.prototype.hasOwnProperty.call(LEGACY_BUSINESS_STAGE_MAP, value)) {
      mapped = LEGACY_BUSINESS_STAGE_MAP[value];
    } else if (COMPANY_SCALE_VALUES.includes(value)) {
      mapped = value;
    } else {
      mapped = null;
    }
    if (!mapped || seen.has(mapped)) continue;
    seen.add(mapped);
    out.push(mapped);
  }
  return out;
}

export function formatPlace(city, country) {
  const c = String(city || '').trim();
  const n = String(country || '').trim();
  if (c && n) return `${c}, ${n}`;
  return c || n || '';
}

export function mergePreferences(raw) {
  const incoming = raw && typeof raw === 'object' ? raw : {};
  const loc = incoming.locationPreferences && typeof incoming.locationPreferences === 'object'
    ? incoming.locationPreferences
    : {};
  const override = loc.locationOverride && typeof loc.locationOverride === 'object' ? loc.locationOverride : {};
  const industries = normalizeIndustries(incoming.industries);
  const technologies = normalizeTechnologies(incoming.technologies);
  return {
    ...EMPTY_PREFERENCES,
    ...incoming,
    industries: industries.values,
    technologies: technologies.values,
    companies: Array.isArray(incoming.companies)
      ? uniqueStrings(incoming.companies, LIMITS.companies, { caseInsensitive: true })
      : [],
    eventTypes: Array.isArray(incoming.eventTypes) ? incoming.eventTypes : [],
    businessStages: normalizeBusinessStages(incoming.businessStages),
    customInterests: mergeInterestTags(incoming.customInterests, [...industries.extras, ...technologies.extras]),
    locationPreferences: {
      ...EMPTY_PREFERENCES.locationPreferences,
      ...loc,
      useCurrentLocation: loc.useCurrentLocation !== false,
      preferredCountries: Array.isArray(loc.preferredCountries) ? loc.preferredCountries : [],
      preferredCities: Array.isArray(loc.preferredCities) ? loc.preferredCities : [],
      locationOverride: {
        country: typeof override.country === 'string' ? override.country : '',
        city: typeof override.city === 'string' ? override.city : '',
      },
      followCurrentMarket: loc.followCurrentMarket === true,
      currentMarketCountry: typeof loc.currentMarketCountry === 'string' ? loc.currentMarketCountry : '',
      currentMarketUpdatedAt: loc.currentMarketUpdatedAt || null,
    },
  };
}

function hasEventLocationOverride(loc) {
  return Boolean(
    loc && loc.useCurrentLocation === false && (loc.locationOverride?.country || loc.locationOverride?.city)
  );
}

export function getEffectiveMarkets(input) {
  const loc = input?.locationPreferences && typeof input.locationPreferences === 'object' ? input.locationPreferences : input || {};
  const selected = uniqueStrings(loc.preferredCountries, LIMITS.preferredCountries);
  if (loc.followCurrentMarket !== true) return selected;
  const current = String(loc.currentMarketCountry || '').trim();
  if (!current) return selected;
  return uniqueStrings([...selected, current], selected.length + 1, { caseInsensitive: true });
}

function hasLocationMarkets(loc) {
  return getEffectiveMarkets(loc).length > 0;
}

export function hasNewsPreferences(prefs, options = {}) {
  if (Number(options.watchlistCount) > 0) return true;
  if (!prefs || typeof prefs !== 'object') return false;
  const lists =
    (Array.isArray(prefs.industries) && prefs.industries.length > 0) ||
    (Array.isArray(prefs.technologies) && prefs.technologies.length > 0) ||
    (Array.isArray(prefs.companies) && prefs.companies.length > 0) ||
    (Array.isArray(prefs.businessStages) && prefs.businessStages.length > 0);
  const text = typeof prefs.customInterests === 'string' && prefs.customInterests.trim().length > 0;
  return lists || text || hasLocationMarkets(prefs.locationPreferences);
}

export function hasEventPreferences(prefs) {
  if (!prefs || typeof prefs !== 'object') return false;
  const lists =
    (Array.isArray(prefs.industries) && prefs.industries.length > 0) ||
    (Array.isArray(prefs.technologies) && prefs.technologies.length > 0) ||
    (Array.isArray(prefs.eventTypes) && prefs.eventTypes.length > 0) ||
    (Array.isArray(prefs.companies) && prefs.companies.length > 0);
  const text = typeof prefs.customInterests === 'string' && prefs.customInterests.trim().length > 0;
  return lists || text || hasEventLocationOverride(prefs.locationPreferences);
}

export function hasMeaningfulInsightPreferences(prefs, options = {}) {
  return hasNewsPreferences(prefs, options) || hasEventPreferences(prefs);
}

/** News V2: event types and Event location alone do not unlock News. */
export function hasNewsV2Preferences(prefs, options = {}) {
  return hasNewsPreferences(prefs, options);
}

const SIGNAL_CAP = 6;

function signalTags(list, kind) {
  const seen = new Set();
  const tags = [];
  for (const raw of list || []) {
    const label = String(raw || '').trim();
    const key = label.toLowerCase();
    if (!label || seen.has(key)) continue;
    seen.add(key);
    tags.push({ label, kind });
    if (tags.length >= SIGNAL_CAP) break;
  }
  return tags;
}

/** Tags for preferences that change the Market Intelligence briefing. */
export function newsSignalTags(preferences) {
  const prefs = preferences && typeof preferences === 'object' ? preferences : {};
  return [
    ...signalTags(prefs.industries, 'industry'),
    ...signalTags(prefs.technologies, 'technology'),
    ...signalTags(prefs.companies, 'company'),
    ...signalTags(prefs.businessStages, 'stage'),
    ...signalTags(parseInterestTags(prefs.customInterests), 'interest'),
    ...signalTags(getEffectiveMarkets(prefs), 'market'),
  ];
}

/**
 * Tags for preferences that change Networking Opportunities.
 * Markets stay off this list. Event place is Near you, or the chosen city and country.
 */
export function eventSignalTags(preferences, eventLocation) {
  const prefs = preferences && typeof preferences === 'object' ? preferences : {};
  const loc = prefs.locationPreferences && typeof prefs.locationPreferences === 'object'
    ? prefs.locationPreferences
    : {};
  const place = [];
  if (loc.useCurrentLocation === false) {
    const chosen = formatPlace(loc.locationOverride?.city, loc.locationOverride?.country);
    if (chosen) place.push({ label: chosen, kind: 'location' });
  } else {
    const near = formatPlace(eventLocation?.city, eventLocation?.country);
    place.push({ label: near ? `Near you · ${near}` : 'Near you', kind: 'location' });
  }
  return [
    ...signalTags(prefs.industries, 'industry'),
    ...signalTags(prefs.technologies, 'technology'),
    ...signalTags(prefs.companies, 'company'),
    ...signalTags(prefs.eventTypes, 'event-type'),
    ...signalTags(parseInterestTags(prefs.customInterests), 'interest'),
    ...place,
  ];
}
